import React from "react";
import { connect } from "react-redux";
// MUI hook for styling function (stateless) components
import { makeStyles } from "@material-ui/core/styles";

// Custom Icons
import { TargetIcon, ThicknessIcon } from "../../constants/customIcons";

// MUI Components
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";

import {
  percentage,
  calculateSwsColor,
  calculateThicknessColor,
} from "../../constants/utilityFunctions";
import { colors } from "../../constants/colors";
import { customTheme } from "../../constants/customTheme";
import {
  decimalPoints,
  heatmapTmodeType,
  heatmapEmodeType,
} from "../../constants/constants";

const SimpleMapRegion = (props) => {
  const useStyles = makeStyles({
    tooltip: {
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      fontSize: "1.3rem",
      "& div": {
        display: "flex",
        alignItems: "center",
        "& svg": {
          marginRight: "5px",
          ...customTheme().iconMedium,
        },
        "& h5": {
          fontWeight: 600,
        },
        "& span": {
          fontSize: "0.5rem",
        },
      },
    },
  });

  const classes = useStyles();

  const handleClick = () => {
    // Dont allow clicking and changing locations while editing location positions on the map
    props.setSeriesVisibility(props.id);
  };

  const [hover, setHover] = React.useState(false);

  const scaledCoordinates = () => {
    if (props.coords.length === 3) {
      // if coords is only three numbers then its a circle and not polygon
      let r = props.coords[0] * (props.imgHeight / 100);
      let cx = props.coords[1] * (props.imgWidth / 100);
      let cy = props.coords[2] * (props.imgHeight / 100);
      return [r.toString(), cx.toString(), cy.toString()];
    } else {
      let scaledCoordinates = [];
      for (var i = 0; i < props.coords.length; i++) {
        if (i % 2 === 0) {
          scaledCoordinates.push(props.coords[i] * (props.imgWidth / 100));
        } else {
          scaledCoordinates.push(props.coords[i] * (props.imgHeight / 100));
        }
      }
      return scaledCoordinates.toString();
    }
  };

  const getStrokeWidth = () => {
    let strokeWidth = 0; // default width of the lines
    if (props.reviewHeatmap.status && props.shearWaveSpeed !== null) {
      strokeWidth = 1;
    } else {
      if (hover) {
        strokeWidth = 2.5;
      } else {
        strokeWidth = 1;
      }
    }
    return strokeWidth;
  };

  const getStrokeColor = () => {
    let strokeColor = "white";
    return strokeColor;
  };

  const getStrokeDash = () => {
    let dashStyle = [props.progress / 2.5, (100 - props.progress) / 5];
    // Show solid if no progress
    if (props.progress === 0) {
      dashStyle = [0, 0];
    }
    return dashStyle.toString();
  };

  const getFillColor = () => {
    let fillColor = colors.facePrimary; // default color
    if (
      props.reviewHeatmap.status &&
      ((props.reviewHeatmap.type === heatmapEmodeType &&
        props.shearWaveSpeed !== null) ||
        (props.reviewHeatmap.type === heatmapTmodeType &&
          props.thickness !== null))
    ) {
      let locationFillColor;
      switch (props.reviewHeatmap.type) {
        case heatmapEmodeType:
          locationFillColor = calculateSwsColor(
            props.shearWaveSpeed,
            props.reviewHeatmap.swsLimit
          );
          break;
        case heatmapTmodeType:
          locationFillColor = calculateThicknessColor(
            props.thickness,
            props.reviewHeatmap.thicknessLimit
          );
          break;
        default:
          if (hover) {
            fillColor = colors.faceTertiary.slice(0, -1) + ", 0.5)";
          } else {
            fillColor = colors.faceTertiary.slice(0, -1) + ", 0.5)";
          }
          break;
      }

      if (hover) {
        fillColor = locationFillColor.slice(0, -1) + ", 0.9)";
      } else {
        fillColor = locationFillColor.slice(0, -1) + ", 0.5)";
      }
    } else {
      if (hover) {
        fillColor = colors.faceTertiary.slice(0, -1) + ", 0.5)";
      } else {
        fillColor = colors.faceTertiary.slice(0, -1) + ", 0.5)";
      }
    }
    return fillColor;
  };

  const handleMouseEnter = () => {
    setHover(true);
    let colorValue =
      props.reviewHeatmap.type === heatmapEmodeType
        ? props.shearWaveSpeed
        : props.reviewHeatmap.type === heatmapTmodeType
        ? props.thickness
        : null;
    props.setColorScaleValue(colorValue);
  };

  const handleMouseLeave = () => {
    setHover(false);
    props.setColorScaleValue(null);
  };

  // tooltip with name and sws info
  const LocationTooltip = () => {
    return (
      <React.Fragment>
        <Typography variant="h6" color="textPrimary">
          {props.name}
        </Typography>
        {props.shearWaveSpeed && (
          <div>
            <TargetIcon color="primary" />
            <Typography variant="h5" color="primary" display="inline">
              {props.shearWaveSpeed.toFixed(decimalPoints)}
              <span> m/s</span>
            </Typography>
          </div>
        )}
        {props.thickness && (
          <div>
            <ThicknessIcon color="secondary" />
            <Typography variant="h5" color="secondary" display="inline">
              {props.thickness.toFixed(decimalPoints)}
              <span> mm</span>
            </Typography>
          </div>
        )}
      </React.Fragment>
    );
  };

  return (
    <Tooltip
      title={<LocationTooltip />}
      enterTouchDelay={200}
      placement="top"
      classes={{ tooltip: classes.tooltip }}
    >
      {props.coords.length === 3 ? (
        <circle
          id={props.id}
          // Style region
          fill={getFillColor()}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeWidth()}
          strokeDasharray={getStrokeDash()}
          // Region coordinates
          r={scaledCoordinates()[0]}
          cx={scaledCoordinates()[1]}
          cy={scaledCoordinates()[2]}
          // Select region/location
          onClick={() => handleClick()}
          // Hover over regions
          style={{ cursor: "pointer" }}
          onMouseOver={() => handleMouseEnter()}
          onMouseLeave={() => handleMouseLeave()}
        ></circle>
      ) : (
        <polygon
          id={props.id}
          // Style region
          fill={getFillColor()}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeWidth()}
          strokeDasharray={getStrokeDash()}
          // Region coordinates
          points={scaledCoordinates()}
          // Select region/location
          onClick={() => handleClick()}
          // Hover over regions
          style={{ cursor: "pointer" }}
          onMouseOver={() => handleMouseEnter()}
          onMouseLeave={() => handleMouseLeave()}
        ></polygon>
      )}
    </Tooltip>
  );
};

const mapStateToProps = (state, ownProps) => {
  const reviewStudy =
    state.availableParticipants.reviewStudies[ownProps.reviewStudyId];
  const location = reviewStudy.locations.byId[ownProps.id];
  const acqTypes = reviewStudy.protocol.acq_types;

  let totalSamplesAcquired = 0;
  let totalSamplesNeeded = 0;

  for (let type of Object.keys(acqTypes)) {
    const samplesAcquired = location.samplesAcquired[type];
    const samplesNeeded = location.samplesNeeded[type];

    totalSamplesNeeded += samplesNeeded;
    if (samplesAcquired < samplesNeeded) {
      totalSamplesAcquired += samplesAcquired;
    } else {
      totalSamplesAcquired += samplesNeeded;
    }
  }

  return {
    name: location.name,
    coords:
      reviewStudy.protocol.locations[ownProps.id].maps[ownProps.currentMapId],
    progress: percentage(totalSamplesAcquired, totalSamplesNeeded),
    shearWaveSpeed: location.shearWaveSpeed.value,
    thickness: location.thickness.value,
    reviewHeatmap: state.utility.reviewHeatmap,
  };
};

export default connect(mapStateToProps)(SimpleMapRegion);
